/** @jsx jsx */
import { Component, type ReactNode, type MouseEvent } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { Item as OptionItem } from '../../../model/index.tsx';
import { Item } from '../../item/main.tsx';

const styles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Temporary workaround to make Emotion and Compiled specificity mesh until `@atlaskit/menu` is migrated to Compiled.
		'&&': {
			backgroundColor: token('color.background.input.pressed'),
			borderRadius: 0,
			height: token('space.600'),
			color: token('color.text'),
			'&:focus': {
				backgroundColor: token('color.background.input.pressed'),
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before]': {
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-description]': {
			color: token('color.text'),
		},
	},
	isFocused: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Temporary workaround to make Emotion and Compiled specificity mesh until `@atlaskit/menu` is migrated to Compiled.
		'&&': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	isSelected: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Temporary workaround to make Emotion and Compiled specificity mesh until `@atlaskit/menu` is migrated to Compiled.
		'&&': {
			backgroundColor: token('color.background.selected'),
			color: token('color.text.brand'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before]': {
			color: token('color.text.brand'),
		},
	},
	isFocusedAndSelected: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Temporary workaround to make Emotion and Compiled specificity mesh until `@atlaskit/menu` is migrated to Compiled.
		'&&': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	isViewAll: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Temporary workaround to make Emotion and Compiled specificity mesh until `@atlaskit/menu` is migrated to Compiled.
		'&&': {
			color: token('color.text.brand'),
		},
	},
});

type InnerRef = (arg1: HTMLElement | null) => void;

type Props = {
	isSelected: boolean;
	isFocused: boolean;
	data: OptionItem;
	innerRef: InnerRef;
	innerProps: {
		onClick: () => void;
	};
	// @ts-expect-error - TS1268 - An index signature parameter type must be 'string', 'number', 'symbol', or a template literal type.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: any]: never;
};

// eslint-disable-next-line jira/react/no-class-components
export default class Option extends Component<Props> {
	onClick = (e: MouseEvent) => {
		if (e.ctrlKey || e.metaKey || e.button === 1) {
			// if item was clicked with cmd or control or middle click -> simply open item in the new window
			return;
		}

		e.preventDefault();
		if (this.props.innerProps && this.props.innerProps.onClick) {
			this.props.innerProps.onClick();
		}
	};

	renderComponent = (props: { className?: string; children: ReactNode }) => {
		const {
			innerProps,
			innerRef,
			data: { url },
		} = this.props;

		return (
			<a
				{...innerProps}
				ref={innerRef}
				href={url}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={props.className}
				onClick={this.onClick}
			>
				{props.children}
			</a>
		);
	};

	render() {
		const { data, isFocused, isSelected } = this.props;
		return (
			<Item
				{...data}
				component={this.renderComponent}
				// @ts-expect-error - TS2322 - Type '{ component: (props: { className: string; children: ReactNode; }) => Element; isFocused: boolean; isSelected: boolean; styles: (state: any) => { '&:focus': { backgroundColor: string; }; ... 5 more ...; height: string; }; ... 6 more ...; isDemo?: boolean | undefined; }' is not assignable to type 'IntrinsicAttributes & ItemProps'.
				isFocused={isFocused}
				isSelected={isSelected}
				css={[
					styles.root,
					isFocused && styles.isFocused,
					isSelected && styles.isSelected,
					isFocused && isSelected && styles.isFocusedAndSelected,
					data.isViewAll && styles.isViewAll,
				]}
			/>
		);
	}
}
